import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Checkbox,
  Paper,
  Chip,
  Button,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../../../../../../theme";

interface Testimonial {
  id: number;
  author_name: string;
  author_description: string;
  company_name: string | null;
  rating: number;
  content: string;
  display_order: number;
}

interface TestimonialsSelectorProps {
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  testimonials: Testimonial[];
}

const TestimonialsSelector: React.FC<TestimonialsSelectorProps> = ({
  open,
  onClose,
  selectedIds,
  onChange,
  testimonials,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tempSelectedIds, setTempSelectedIds] = useState<number[]>(selectedIds);

  useEffect(() => {
    if (open) setTempSelectedIds(selectedIds);
  }, [open, selectedIds]);

  const handleToggle = (id: number) => {
    const isCurrentlySelected = tempSelectedIds.includes(id);
    if (!isCurrentlySelected && tempSelectedIds.length >= 3) return;
    setTempSelectedIds((prev) =>
      isCurrentlySelected ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    onChange(tempSelectedIds);
    onClose();
  };

  const isSelected = (id: number) => tempSelectedIds.includes(id);
  const isDisabled = (id: number) => tempSelectedIds.length >= 3 && !isSelected(id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6">Select Testimonials</Typography>
            <Typography variant="caption" color="text.secondary">
              Choose up to 3 testimonials to display in your proposal
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Chip
              label={`${tempSelectedIds.length}/3 selected`}
              size="small"
              sx={{
                bgcolor:
                  tempSelectedIds.length === 3
                    ? colors.primary[400]
                    : theme.palette.action.hover,
                color:
                  tempSelectedIds.length === 3
                    ? colors.primary[900]
                    : "text.secondary",
                fontWeight: 600,
              }}
            />
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {testimonials.length === 0 ? (
          <Typography variant="body2" sx={{ textAlign: "center", py: 8 }}>
            No testimonials available.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 500, overflowY: "auto", pr: 1 }}>
            {testimonials.map((t) => (
              <Paper
                key={t.id}
                onClick={() => !isDisabled(t.id) && handleToggle(t.id)}
                sx={{
                  p: 2,
                  mb: 2,
                  border: isSelected(t.id)
                    ? `2px solid ${colors.primary[400]}`
                    : `1px solid ${theme.palette.divider}`,
                  bgcolor: isDisabled(t.id)
                    ? theme.palette.action.disabledBackground
                    : theme.palette.background.paper,
                  cursor: isDisabled(t.id) ? "not-allowed" : "pointer",
                  opacity: isDisabled(t.id) ? 0.6 : 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: isDisabled(t.id)
                      ? theme.palette.action.disabledBackground
                      : theme.palette.action.hover,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Checkbox
                    checked={isSelected(t.id)}
                    disabled={isDisabled(t.id)}
                    onChange={() => handleToggle(t.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: colors.primary[400],
                      "&.Mui-checked": { color: colors.primary[400] },
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        lineHeight: 1.6,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      “{t.content}”
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      — {t.author_name}
                      {t.company_name && `, ${t.company_name}`}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: colors.primary[400],
            "&:hover": { bgcolor: colors.primary[500] },
          }}
        >
          Save Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestimonialsSelector;